/**
 * Cohesive brand loader, the AssembleMark.
 *
 * Ported verbatim from cohesive-design-system/showcase/lib/brand-components.jsx
 * (`AssembleMark`, promoted to canon by RFC 003). The three shapes of the mark
 * glide in from their own centroids, lock into the lockup, hold, and
 * re-assemble. Geometry, viewBox, per-shape transform-origins and the 3.2s
 * cadence are the design system's; the keyframes and the reduced-motion
 * fallback live with the other motion tokens in cohesive-tokens.css.
 *
 * Fill rides `currentColor` so the mark works on any ground.
 *
 * Scope, from the same RFC: full-screen Brand/Editorial transition beats only,
 * never inline in Functional UI. A mechanical wait inside a surface is the
 * `Spinner`; agent work in progress is a `WorkingSignal`.
 */

import { cn } from "@/lib/utils";

/** The mark taken apart: disc, bar, petal. */
const ASSEMBLE_A =
  "M18.16 6.572H18.205V42.772H18.159C8.136 42.773 0 34.664 0 24.673C0 14.683 8.136 6.572 18.16 6.572Z";
const ASSEMBLE_B =
  "M30.497 0L48.841 18.283C49.5178 18.9575 50.0548 19.7589 50.4213 20.6414C50.7877 21.5239 50.9763 22.47 50.9763 23.4255C50.9763 24.381 50.7877 25.3271 50.4213 26.2096C50.0548 27.0921 49.5178 27.8935 48.841 28.568C45.995 31.405 41.371 31.408 38.522 28.568L20.179 10.285L30.497 0Z";
const ASSEMBLE_C =
  "M26.603 58.997L16.287 48.715L36.01 29.056L36.244 29.29C41.808 34.835 41.808 43.841 36.244 49.387L26.6 59L26.603 58.997Z";

/** `calc(var(--size-16) * 1.6)` in the design system, where `--size-16` is 64px. */
const MARK_WIDTH = 102.4;

export function BrandLoader({
  className,
  label = "Loading",
}: {
  className?: string;
  /** Announced to assistive technology; the mark itself is decorative. */
  label?: string;
}) {
  return (
    <span className={cn("inline-flex", className)} role="status">
      <svg
        viewBox="-30 -30 111 119"
        aria-hidden="true"
        className="h-auto overflow-visible fill-current"
        style={{ width: MARK_WIDTH }}
      >
        <path
          className="animate-assemble-a motion-reduce:animate-none"
          style={{ transformOrigin: "9.1px 24.7px" }}
          d={ASSEMBLE_A}
        />
        <path
          className="animate-assemble-b motion-reduce:animate-none"
          style={{ transformOrigin: "35.6px 14.3px" }}
          d={ASSEMBLE_B}
        />
        <path
          className="animate-assemble-c motion-reduce:animate-none"
          style={{ transformOrigin: "26.2px 44px" }}
          d={ASSEMBLE_C}
        />
      </svg>
      <span className="sr-only">{label}</span>
    </span>
  );
}
